import PropTypes from 'prop-types';
import React, { lazy, Suspense } from 'react';
import { AIChatProvider } from '../../../contexts/AIChatProvider';
import './BaseChatbotWidget.scss';

// Lazy load the heavy UI component - non-AI users don't pay bundle/runtime cost
const ChatbotWidgetCore = lazy(() => import('./ChatbotWidgetCore'));

/**
 * Chatbot widget that provides Context wrapper for the chatbot functionality
 *
 * Architecture:
 * - Provider always mounts (context available for hooks)
 * - UI only loads when AI is enabled (lazy loaded)
 * - When isAIEnabled=false: context exposes no-op actions, zero async work
 */
function ChatbotWidget({ initialState, isAIEnabled = true, ...props }) {
  return (
    <AIChatProvider initialState={initialState} isAIEnabled={isAIEnabled}>
      {/* Mount UI when AI is enabled - hideFloatingButton only controls button visibility, not UI mounting */}
      {/* ChatbotWidgetCore handles hideFloatingButton internally to hide the floating trigger button */}
      {/* This allows chatbot to be opened programmatically via openChatbot() even when button is hidden */}
      {isAIEnabled && (
        <Suspense fallback={null}>
          <ChatbotWidgetCore {...props} />
        </Suspense>
      )}
      {!isAIEnabled && props.children}
    </AIChatProvider>
  );
}

ChatbotWidget.propTypes = {
  /** Whether AI features are enabled (controls provider behavior and UI loading) */
  isAIEnabled: PropTypes.bool,
  chatService: PropTypes.object,
  historyButtonLabel: PropTypes.string,
  buttonIconName: PropTypes.string,
  errorMessage: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  getChatContextData: PropTypes.func,
  animationDuration: PropTypes.number,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  isControlled: PropTypes.bool,
  isOpenControlled: PropTypes.bool,
  focusOnOpen: PropTypes.bool,
  /** Per-slice seeding: { shell, agentConfig, practiceSession, conversation } */
  initialState: PropTypes.shape({
    agentConfig: PropTypes.object,
    conversation: PropTypes.object,
    practiceSession: PropTypes.object,
    shell: PropTypes.object,
  }),
  hideFloatingButton: PropTypes.bool,
};

ChatbotWidget.defaultProps = {
  isAIEnabled: true,
  historyButtonLabel: '📜 History',
  buttonIconName: 'chat',
  errorMessage: 'An error occurred. Please try again.',
  className: '',
  chatService: null,
  children: null,
  animationDuration: 500,
  onOpen: null,
  onClose: null,
  isControlled: false,
  isOpenControlled: undefined,
  focusOnOpen: true,
  initialState: null,
};

export default ChatbotWidget;
